import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCheck,
  faExclamationTriangle,
  faSpinner,
  faCalendarAlt,
  faCloudDownloadAlt,
  faDatabase,
} from '@fortawesome/free-solid-svg-icons';
import './SelleriseUploadPopup.css';

const SelleriseUploadPopup = ({
  showPopup,
  closePopup,
  mode,
  onModeChange,
  // Weekly
  startDate, setStartDate,
  endDate,   setEndDate,
  // Monthly
  selectedYear,   setSelectedYear,
  selectedMonths,
  toggleMonth,
  selectAllMonths,
  clearAllMonths,
  monthNames,
  // Shared
  scrapedRows,
  scrapeStatus,
  uploadStatus,
  uploadErrors = [],
  handleFetch,
  handleUpload,
}) => {
  if (!showPopup) return null;

  const isFetching  = scrapeStatus?.type === 'loading';
  const isUploading = uploadStatus?.type  === 'loading';
  const hasFetched  = scrapedRows.length > 0;
  const uploadDone  = uploadStatus?.type  === 'success';
  const isWeekly    = mode === 'weekly';
  const isMonthly   = mode === 'monthly';

  const totalUnits    = scrapedRows.reduce((sum, r) => sum + (r.units || 0), 0);
  const uniqueWeeks   = isWeekly
    ? [...new Set(scrapedRows.map(r => `${r.weeknumber}-${r.year}`))].length
    : 0;
  const uniqueMonths  = isMonthly
    ? [...new Set(scrapedRows.map(r => `${r.year}-${r.month}`))].length
    : 0;

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i);

  // Короткие названия для сетки
  const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="popup-overlay">
      <div className="popup-content sellerise-popup">

        {/* ── Header ── */}
        <div className="popup-header">
          <h3>
            <FontAwesomeIcon icon={faCloudDownloadAlt} style={{ marginRight: 8 }} />
            Auto-fetch from Sellerise
          </h3>
          <button className="close-btn" onClick={closePopup}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="popup-body">

          {/* ── Mode toggle ── */}
          <div className="mode-toggle">
            <button
              className={`mode-btn ${isWeekly ? 'active' : ''}`}
              onClick={() => onModeChange('weekly')}
              disabled={isFetching || isUploading}
            >
              Weekly Report
            </button>
            <button
              className={`mode-btn ${isMonthly ? 'active' : ''}`}
              onClick={() => onModeChange('monthly')}
              disabled={isFetching || isUploading}
            >
              Monthly Report
            </button>
          </div>

          {/* ── Instructions ── */}
          <div className="upload-instructions">
            <p><strong>How it works:</strong></p>
            <ul>
              {isWeekly  && <li>Select a date range (up to 12 weeks)</li>}
              {isMonthly && <li>Select a year and one or more months</li>}
              <li>Click <strong>Fetch Data</strong> — the server will log into Sellerise and download reports automatically</li>
              <li>Review the preview, then click <strong>Save to Database</strong></li>
            </ul>
            <p style={{ color: '#e67e22', marginTop: 6 }}>
              ⚠️ If 2-factor auth is required, the first fetch may take ~30 seconds.
              Subsequent fetches reuse the saved session.
            </p>
          </div>

          {/* ── Weekly: date range ── */}
          {isWeekly && (
            <div className="date-range-section">
              <div className="date-field">
                <label>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 6 }} />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isFetching || isUploading}
                />
              </div>
              <div className="date-separator">→</div>
              <div className="date-field">
                <label>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 6 }} />
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isFetching || isUploading}
                />
              </div>
            </div>
          )}

          {/* ── Monthly: year + month grid ── */}
          {isMonthly && (
            <div className="monthly-picker">
              {/* Year selector */}
              <div className="monthly-picker-header">
                <div className="date-field year-field">
                  <label>
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 6 }} />
                    Year
                  </label>
                  <select
                    className="year-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    disabled={isFetching || isUploading}
                  >
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                {/* Select all / Clear */}
                <div className="month-select-actions">
                  <button
                    className="btn-month-action"
                    onClick={selectAllMonths}
                    disabled={isFetching || isUploading}
                  >
                    All
                  </button>
                  <button
                    className="btn-month-action"
                    onClick={clearAllMonths}
                    disabled={isFetching || isUploading}
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Month grid 4×3 */}
              <div className="month-grid">
                {MONTH_SHORT.map((name, idx) => {
                  const monthNum  = idx + 1;
                  const isSelected = selectedMonths.includes(monthNum);
                  return (
                    <button
                      key={monthNum}
                      className={`month-cell ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleMonth(monthNum)}
                      disabled={isFetching || isUploading}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>

              {selectedMonths.length > 0 && (
                <div className="months-selected-hint">
                  {selectedMonths.length} month{selectedMonths.length > 1 ? 's' : ''} selected:&nbsp;
                  {selectedMonths.map(m => MONTH_SHORT[m - 1]).join(', ')}
                </div>
              )}
            </div>
          )}

          {/* ── Progress / scrape status ── */}
          {scrapeStatus && (
            <div className={`upload-status ${scrapeStatus.type}`}>
              <div className="status-icon">
                {scrapeStatus.type === 'loading' && <FontAwesomeIcon icon={faSpinner} spin />}
                {scrapeStatus.type === 'success' && <FontAwesomeIcon icon={faCheck} />}
                {scrapeStatus.type === 'error'   && <FontAwesomeIcon icon={faExclamationTriangle} />}
              </div>
              <div className="status-message-block">
                <div className="status-message">{scrapeStatus.message}</div>
                {scrapeStatus.type === 'loading' && typeof scrapeStatus.percent === 'number' && (
                  <div className="progress-bar-wrapper">
                    <div className="progress-bar-fill" style={{ width: `${scrapeStatus.percent}%` }} />
                    <span className="progress-percent">{scrapeStatus.percent}%</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Data preview ── */}
          {hasFetched && (
            <div className="data-preview">
              <h4>
                Data Preview
                <span className="preview-meta">
                  {scrapedRows.length} records
                  {isWeekly  && ` · ${uniqueWeeks} week${uniqueWeeks !== 1 ? 's' : ''}`}
                  {isMonthly && ` · ${uniqueMonths} month${uniqueMonths !== 1 ? 's' : ''}`}
                  {' · '}{totalUnits.toLocaleString()} total units
                </span>
              </h4>
              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>ASIN</th>
                      {isWeekly  && <th>Week</th>}
                      {isWeekly  && <th>Year</th>}
                      {isWeekly  && <th>Period</th>}
                      {isMonthly && <th>Month</th>}
                      {isMonthly && <th>Year</th>}
                      <th>Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scrapedRows.slice(0, 10).map((row, idx) => (
                      <tr key={idx}>
                        <td><code>{row.asin}</code></td>
                        {isWeekly  && <td>{row.weeknumber}</td>}
                        {isWeekly  && <td>{row.year}</td>}
                        {isWeekly  && (
                          <td style={{ fontSize: '0.8em', color: '#888' }}>
                            {row.weekStart} – {row.weekEnd}
                          </td>
                        )}
                        {isMonthly && <td>{MONTH_SHORT[(row.month ?? 1) - 1]}</td>}
                        {isMonthly && <td>{row.year}</td>}
                        <td><strong>{row.units?.toLocaleString()}</strong></td>
                      </tr>
                    ))}
                    {scrapedRows.length > 10 && (
                      <tr>
                        <td colSpan={isWeekly ? 5 : 4} className="preview-more">
                          … and {scrapedRows.length - 10} more records
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Upload status ── */}
          {uploadStatus && (
            <div className={`upload-status ${uploadStatus.type}`}>
              <div className="status-icon">
                {uploadStatus.type === 'loading' && <FontAwesomeIcon icon={faSpinner} spin />}
                {uploadStatus.type === 'success' && <FontAwesomeIcon icon={faCheck} />}
                {uploadStatus.type === 'error'   && <FontAwesomeIcon icon={faExclamationTriangle} />}
              </div>
              <div className="status-message">{uploadStatus.message}</div>
            </div>
          )}

          {/* ── Mapping errors ── */}
          {uploadErrors.length > 0 && (
            <div className="upload-errors-block">
              <div className="upload-errors-header">
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#e67e22', marginRight: 6 }} />
                <strong>{uploadErrors.length} ASINs not found in DB mapping:</strong>
              </div>
              <div className="upload-errors-list">
                {uploadErrors.map((msg, i) => (
                  <div key={i} className="upload-error-item">{msg}</div>
                ))}
              </div>
              <p className="upload-errors-hint">
                These ASINs exist in Sellerise but are not mapped in your database tables (wildmicr / sets / individualpackets / product_alternate_ids).
              </p>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="popup-footer">
          <button className="btn-cancel" onClick={closePopup} disabled={isFetching || isUploading}>
            {uploadDone ? 'Close' : 'Cancel'}
          </button>

          {!uploadDone && (
            <button
              className="btn-fetch"
              onClick={handleFetch}
              disabled={
                isFetching || isUploading ||
                (isWeekly  && (!startDate || !endDate)) ||
                (isMonthly && selectedMonths.length === 0)
              }
            >
              {isFetching ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Fetching…</>
              ) : (
                <><FontAwesomeIcon icon={faCloudDownloadAlt} /> {hasFetched ? 'Re-fetch' : 'Fetch Data'}</>
              )}
            </button>
          )}

          {hasFetched && !uploadDone && (
            <button
              className="btn-upload"
              onClick={handleUpload}
              disabled={isFetching || isUploading}
            >
              {isUploading ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</>
              ) : (
                <><FontAwesomeIcon icon={faDatabase} /> Save to Database</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelleriseUploadPopup;